import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useResultsStoryAssets } from '@sorare/core/src/components/animations/AnimatedStory/useResultsStoryAssets';
import { ANY_SPORT_MY_CARDS_BOXES } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { FixtureOverviewTasks } from 'components/fixture/FixtureTeamsOverview/FixtureOverview/FixtureOverviewTasks';
import { RewardConfigsOverview } from 'components/rewards/RewardConfigsOverview';
import { RewardConfigsOverviewHeader } from 'components/rewards/RewardConfigsOverviewHeader';
import { LastFixtureStoryStep } from 'components/so5/LastFixtureStory/LastFixtureStoryStep';
import type { StoryState } from 'components/so5/LastFixtureStory/types';
import { useClaimRewards } from 'hooks/so5/useClaimRewards';

import { Cta } from '../LastFixtureStoryStep/Cta';
import { Title } from '../LastFixtureStoryStep/Title';
import { VerificationBanner } from '../VerificationBanner';
import { DeliverableItemBlock } from './DeliverableItemBlock';
import { TaskRewardBlock } from './TaskRewardBlock';
import type {
  RewardsRecap_anyRewardConfigInterface,
  RewardsRecap_rarityRewardConfigs,
  RewardsRecap_so5Fixture,
  RewardsRecap_so5Reward,
  RewardsRecap_taskInterface,
} from './__generated__/index.graphql';
import type { RewardByType } from './useRewardsVerification';
import { useRewardsVerification } from './useRewardsVerification';

const RewardsRecapWrapper = styled(Vertical)`
  @media ${laptopAndAbove} {
    min-width: 420px;
  }
`;
const FullWidthVertical = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
  max-width: 600px;
`;

type CtaProps = {
  box: number;
  card: number;
  onNext?: () => void;
  onClose?: () => void;
};
const SeeBoxOrRevealStepCta = ({ box, card, onNext, onClose }: CtaProps) => {
  const { generateSportContextPath } = useSportContext();
  const navigate = useNavigate();

  if (card > 0 && onNext) {
    return (
      <Button size="medium" fullWidth color="secondary" onClick={onNext}>
        <FormattedMessage
          id="RewardsRecap.cta.revealMyCard"
          defaultMessage="Reveal {card, plural, one {card} other {cards}}"
          values={{ card }}
        />
      </Button>
    );
  }
  if (box > 0 && !onNext) {
    return (
      <Button
        size="medium"
        fullWidth
        color="secondary"
        onClick={async () => {
          await onClose?.();
          navigate(generateSportContextPath(ANY_SPORT_MY_CARDS_BOXES));
        }}
      >
        <FormattedMessage
          id="CardRewardsReveal.cta.goToMyBoxes"
          defaultMessage="Go to My Boxes"
        />
      </Button>
    );
  }
  return null;
};

const getAutoClaimableRewards = <T extends object>(
  claimableRewardsByType: RewardByType<T>,
  useRewardChoicesV2: boolean
) => {
  if (useRewardChoicesV2) {
    const {
      total,
      card,
      cash,
      shards,
      ...cardCashShardsExcludedClaimableRewards
    } = claimableRewardsByType;
    return cardCashShardsExcludedClaimableRewards;
  }
  const { total, card, ...cardExcludedClaimableRewards } =
    claimableRewardsByType;
  return cardExcludedClaimableRewards;
};

// This function is replacing the 'so5Rewards[number]['rewardConfigs'][number]' object
// by the associated 'so5Rewards[number]['alternativeRewardConfigs'][number]['rewardConfigs'][number]'
// when the user select the alternative reward choice; we have to do this because
// so5Rewards are memoized in a useState to prevent the claim mutation to close the rewards flow
const extractAndReplaceRewardConfigs = (
  so5Rewards: RewardsRecap_so5Reward[],
  so5RewardsWithSignedAlternative?: RewardWithSignedAlternative[]
):
  | RewardsRecap_so5Reward['rewardConfigs']
  | NonNullable<
      RewardsRecap_so5Reward['alternativeRewardConfigs']
    >[number]['rewardConfigs'] => {
  if (!so5RewardsWithSignedAlternative) {
    return so5Rewards.flatMap(({ rewardConfigs }) => rewardConfigs);
  }

  return [...so5Rewards].flatMap(so5Reward => {
    const selectedAlternativeReward = so5Reward.alternativeRewardConfigs?.find(
      ({ signedAlternativeRewardConfigs }) => {
        const associatedso5RewardWithSignedAlternative =
          so5RewardsWithSignedAlternative.find(
            ({ so5RewardId }) => so5RewardId === so5Reward.id
          );

        return (
          !!associatedso5RewardWithSignedAlternative &&
          associatedso5RewardWithSignedAlternative.signedAlternativeRewardConfigs ===
            signedAlternativeRewardConfigs
        );
      }
    );

    if (!selectedAlternativeReward) {
      return so5Reward.rewardConfigs;
    }

    return selectedAlternativeReward.rewardConfigs;
  });
};

type RewardWithSignedAlternative = {
  so5RewardId: string;
  signedAlternativeRewardConfigs: string | null;
};
type Props = {
  so5Fixture?: RewardsRecap_so5Fixture;
  so5Rewards: RewardsRecap_so5Reward[];
  boostedRewardConfigsByRarity?: RewardsRecap_rarityRewardConfigs[];
  so5RewardsWithSignedAlternative?: RewardWithSignedAlternative[];
  tasks?: RewardsRecap_taskInterface[];
  onNext?: () => void;
  onClose: ComponentProps<typeof LastFixtureStoryStep>['onClose'];
  from?: StoryState;
};
export const RewardsRecap = ({
  from: fromProp,
  so5Fixture,
  so5Rewards,
  boostedRewardConfigsByRarity,
  so5RewardsWithSignedAlternative,
  tasks,
  onNext,
  onClose,
}: Props) => {
  const {
    flags: { useRewardChoicesV2 = false },
  } = useFeatureFlags();

  const from = useRef(fromProp);
  const { claimableRewardsByType, rewardsByType, verificationNeeded } =
    useRewardsVerification(so5Rewards);

  const [claimed, setClaimed] = useState(false);
  const [claimRewards] = useClaimRewards();

  const { intro, tunnel } = useResultsStoryAssets();

  const { currentUser } = useCurrentUserContext();

  const deliverableItemOrders = claimed
    ? currentUser?.unclaimedDeliverableItemOrders || []
    : [];

  const autoClaimableRewards = getAutoClaimableRewards(
    claimableRewardsByType,
    !!useRewardChoicesV2
  );

  const autoClaimableRewardIds = Object.values(autoClaimableRewards).flatMap(
    reward => reward.map(({ id }) => id)
  );

  if (!claimed && autoClaimableRewardIds.length) {
    claimRewards({ so5RewardIds: autoClaimableRewardIds });
    setClaimed(true);
  }

  const taskRewards = tasks?.flatMap(task => task.rewardConfigs);
  const rewardBoxesCount =
    rewardsByType.box.length +
    (taskRewards?.filter(isTypeFn('ProbabilisticBundleRewardConfig')).length ||
      0);

  const hasCardRewards = rewardsByType.card.length > 0;
  const cta = ((!hasCardRewards && !!so5Fixture) ||
    hasCardRewards ||
    rewardBoxesCount > 0) && (
    <>
      {!hasCardRewards && !!so5Fixture && (
        <Cta
          so5FixtureSlug={so5Fixture?.slug}
          eventType={so5Fixture?.type}
          onClose={onClose}
        />
      )}
      {hasCardRewards || rewardBoxesCount > 0 ? (
        <SeeBoxOrRevealStepCta
          card={rewardsByType.card.length}
          box={rewardBoxesCount}
          onNext={onNext}
          onClose={onClose}
        />
      ) : null}
    </>
  );

  const rewardConfigs = extractAndReplaceRewardConfigs(
    so5Rewards,
    so5RewardsWithSignedAlternative
  );

  return (
    <LastFixtureStoryStep
      title={<Title type="rewardsRecap" so5Fixture={so5Fixture} />}
      video={{
        asset: from.current === 'intro' ? intro : tunnel,
        color: 'var(--c-neutral-600)',
      }}
      onNext={onNext}
      onClose={onClose}
      cta={cta}
      banner={<VerificationBanner verificationNeeded={verificationNeeded} />}
    >
      <FullWidthVertical>
        <RewardsRecapWrapper>
          <RewardConfigsOverviewHeader
            so5Fixture={so5Fixture}
            rewardConfigs={rewardConfigs}
            boostedRewardConfigsByRarity={boostedRewardConfigsByRarity}
            verificationNeeded={verificationNeeded}
            onClose={onClose}
            onlyRewards
          />
          <RewardConfigsOverview
            rewardConfigs={rewardConfigs}
            boostedRewardConfigsByRarity={boostedRewardConfigsByRarity || []}
            so5Lineups={[]}
            prepend={
              !!so5Fixture && <FixtureOverviewTasks so5Fixture={so5Fixture} />
            }
          />
        </RewardsRecapWrapper>
        <TaskRewardBlock taskRewards={taskRewards} />
        <DeliverableItemBlock
          deliverableItemOrders={deliverableItemOrders}
          deliverableItems={rewardsByType.deliverableItem
            .flatMap(so5Reward => so5Reward.rewards)
            .filter(reward => isType(reward, 'DeliverableItemReward'))}
        />
      </FullWidthVertical>
    </LastFixtureStoryStep>
  );
};

const anyRewardConfigInterfaceFragment = gql`
  fragment RewardsRecap_anyRewardConfigInterface on AnyRewardConfigInterface {
    id
    boostedRewardConfigs {
      id
      ...RewardConfigsOverview_anyRewardConfigInterface
    }
    ...RewardConfigsOverview_anyRewardConfigInterface
  }
  ${RewardConfigsOverview.fragments.anyRewardConfigInterface}
` as TypedDocumentNode<RewardsRecap_anyRewardConfigInterface>;

RewardsRecap.fragments = {
  rarityRewardConfigs: gql`
    fragment RewardsRecap_rarityRewardConfigs on RarityRewardConfigs {
      ...RewardConfigsOverview_rarityRewardConfigs
    }
    ${RewardConfigsOverview.fragments.rarityRewardConfigs}
  ` as TypedDocumentNode<RewardsRecap_rarityRewardConfigs>,
  so5Reward: gql`
    fragment RewardsRecap_so5Reward on So5Reward {
      slug
      id
      rewards {
        id
        ...DeliverableItemBlock_deliverableItemReward
      }
      alternativeRewardConfigs {
        signedAlternativeRewardConfigs
        rewardConfigs {
          id
          ...RewardsRecap_anyRewardConfigInterface
        }
      }
      rewardConfigs {
        id
        ...RewardsRecap_anyRewardConfigInterface
      }
      ...useRewardsVerification_so5Reward
    }
    ${useRewardsVerification.fragments.so5Reward}
    ${DeliverableItemBlock.fragments.deliverableItemReward}
    ${anyRewardConfigInterfaceFragment}
  ` as TypedDocumentNode<RewardsRecap_so5Reward>,
  taskInterface: gql`
    fragment RewardsRecap_taskInterface on TaskInterface {
      id
      rewardConfigs {
        id
        ...TaskRewardBlock_anyRewardConfigInterface
      }
    }
    ${TaskRewardBlock.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<RewardsRecap_taskInterface>,
  so5Fixture: gql`
    fragment RewardsRecap_so5Fixture on So5Fixture {
      slug
      type
      gameWeek
      mySo5Lineups {
        id
        ...RewardConfigsOverview_so5Lineup
      }
      ...FixtureOverviewTasks_so5Fixture
      ...Title_so5Fixture
      ...RewardConfigsOverviewHeader_so5Fixture
    }
    ${FixtureOverviewTasks.fragments.so5Fixture}
    ${RewardConfigsOverviewHeader.fragments.so5Fixture}
    ${RewardConfigsOverview.fragments.so5Lineup}
    ${Title.fragments.so5Fixture}
  ` as TypedDocumentNode<RewardsRecap_so5Fixture>,
};
